import type { ExcalElement } from "./excalidrawTypes";

// The one merge rule for Excalidraw scenes, shared by both sync paths: the live
// WebSocket relay (collab.ts) and the durable Replicache rebase (ExcalidrawView).
// Keeping a single function means the in-memory and persisted views can't drift
// apart by using different reconciliation semantics.

/** True if `incoming` should win over `local`: higher Excalidraw `version`
 *  wins; ties broken by the smaller `versionNonce` (Excalidraw's own
 *  deterministic rule).  Deletions ride along as higher-version `isDeleted`
 *  elements, so they win over an older live copy like any other update. */
export function isElementNewer(
  incoming: ExcalElement,
  local: ExcalElement,
): boolean {
  const rv = incoming.version ?? 0;
  const lv = local.version ?? 0;
  if (rv !== lv) return rv > lv;
  return (incoming.versionNonce ?? 0) < (local.versionNonce ?? 0);
}

/** Element-level last-write-wins merge.  Unions by id so no element is dropped
 *  just because it's missing from one side — the bug that made shapes vanish
 *  when a durable row arrived without elements a peer had drawn.  A deletion is
 *  carried as an `isDeleted` tombstone, not an absence, so union is safe. */
export function reconcileElements(
  local: readonly ExcalElement[],
  incoming: readonly ExcalElement[],
): ExcalElement[] {
  const byId = new Map<string, ExcalElement>();
  for (const el of local) byId.set(el.id, el);
  for (const el of incoming) {
    const cur = byId.get(el.id);
    if (!cur || isElementNewer(el, cur)) byId.set(el.id, el);
  }
  return [...byId.values()];
}
